import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Badge } from "./ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "./ui/dialog";
import { Label } from "./ui/label";
import {
  Search, Building2, Plus, ChevronDown, Check, Bell,
  CalendarDays, ClipboardCheck, Coins, KeyRound, UserMinus, Trash2,
} from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";

const eventIcons = {
  leave_submitted: CalendarDays,
  leave_approved: CalendarDays,
  leave_rejected: CalendarDays,
  onboarding_started: ClipboardCheck,
  payslip_ready: Coins,
  login_created: KeyRound,
  employee_left: UserMinus,
};

const eventColors = {
  leave_submitted: "text-amber-600 bg-amber-100",
  leave_approved: "text-green-600 bg-green-100",
  leave_rejected: "text-red-600 bg-red-100",
  onboarding_started: "text-teal-600 bg-teal-100",
  payslip_ready: "text-emerald-600 bg-emerald-100",
  login_created: "text-blue-600 bg-blue-100",
  employee_left: "text-slate-600 bg-slate-100",
};

const Header = () => {
  const { user, currentBusiness, businesses, switchBusiness, addBusiness, removeBusiness } = useAuth();
  const [newBusinessName, setNewBusinessName] = useState("");
  const [isCreating, setIsCreating] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [businessToDelete, setBusinessToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);

  const isEmployee = user?.role === "employee";

  const fetchNotifications = useCallback(async () => {
    try {
      const res = await api.get("/notifications?limit=20");
      setNotifications(res.data.notifications || []);
      setUnreadCount(res.data.unread_count || 0);
    } catch {}
  }, []);

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000); // Poll every 30s
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  const markAsRead = async (id) => {
    try {
      await api.put(`/notifications/${id}/read`);
      fetchNotifications();
    } catch {}
  };

  const markAllRead = async () => {
    try {
      await api.put("/notifications/read-all");
      fetchNotifications();
    } catch {}
  };

  const handleCreateBusiness = async (e) => {
    e.preventDefault();
    if (!newBusinessName.trim()) return;
    setIsCreating(true);
    try {
      const response = await api.post("/businesses", { name: newBusinessName });
      addBusiness(response.data);
      setNewBusinessName("");
      setDialogOpen(false);
      toast.success("Business created successfully!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create business");
    } finally {
      setIsCreating(false);
    }
  };

  const handleDeleteBusiness = async () => {
    if (!businessToDelete) return;
    setIsDeleting(true);
    try {
      await api.delete(`/businesses/${businessToDelete.id}`);
      removeBusiness(businessToDelete.id);
      setDeleteDialogOpen(false);
      setBusinessToDelete(null);
      toast.success(`Business "${businessToDelete.name}" deleted successfully`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete business");
    } finally {
      setIsDeleting(false);
    }
  };

  const openDeleteDialog = (e, business) => {
    e.stopPropagation();
    setBusinessToDelete(business);
    setDeleteDialogOpen(true);
  };

  const timeAgo = (dateStr) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return "now";
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return `${Math.floor(hrs / 24)}d ago`;
  };

  return (
    <header className="sticky top-0 z-40 glass border-b border-slate-100">
      <div className="flex items-center justify-between h-16 px-6 lg:px-8">
        {/* Search */}
        <div className="hidden md:flex items-center flex-1 max-w-md">
          <div className="relative w-full">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input placeholder="Search..." className="pl-10 bg-white/50 border-slate-200 rounded-full" data-testid="header-search" />
          </div>
        </div>

        <div className="flex items-center gap-3 ml-auto">
          {/* Notifications Bell */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="relative rounded-full" data-testid="notifications-btn">
                <Bell className="h-5 w-5 text-slate-500" />
                {unreadCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 h-5 w-5 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center"
                    data-testid="unread-badge">
                    {unreadCount > 9 ? "9+" : unreadCount}
                  </span>
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-80 max-h-[400px] overflow-y-auto">
              <div className="flex items-center justify-between px-3 py-2">
                <DropdownMenuLabel className="p-0">Notifications</DropdownMenuLabel>
                {unreadCount > 0 && (
                  <button onClick={markAllRead} className="text-xs text-primary hover:underline" data-testid="mark-all-read">
                    Mark all read
                  </button>
                )}
              </div>
              <DropdownMenuSeparator />
              {notifications.length === 0 ? (
                <div className="py-8 text-center text-sm text-slate-400">No notifications</div>
              ) : (
                notifications.slice(0, 15).map(n => {
                  const Icon = eventIcons[n.event_type] || Bell;
                  const colors = eventColors[n.event_type] || "text-slate-600 bg-slate-100";
                  return (
                    <DropdownMenuItem
                      key={n.id}
                      className={`flex items-start gap-3 p-3 cursor-pointer ${!n.is_read ? "bg-primary/5" : ""}`}
                      onClick={() => markAsRead(n.id)}
                      data-testid={`notif-${n.id}`}
                    >
                      <div className={`p-2 rounded-lg flex-shrink-0 ${colors}`}>
                        <Icon className="h-4 w-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm leading-tight ${!n.is_read ? "font-semibold text-slate-800" : "text-slate-600"}`}>
                          {n.title}
                        </p>
                        <p className="text-xs text-slate-400 mt-0.5 line-clamp-2">{n.message}</p>
                        <p className="text-[10px] text-slate-300 mt-1">{timeAgo(n.created_at)}</p>
                      </div>
                      {!n.is_read && <div className="w-2 h-2 rounded-full bg-primary flex-shrink-0 mt-1.5" />}
                    </DropdownMenuItem>
                  );
                })
              )}
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Business Switcher - Admin only */}
          {!isEmployee && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="gap-2 rounded-full border-slate-200 bg-white/50" data-testid="business-switcher">
                  <Building2 className="h-4 w-4 text-primary" />
                  <span className="max-w-[120px] truncate">{currentBusiness?.name || "Select Business"}</span>
                  <ChevronDown className="h-4 w-4 text-slate-400" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-64">
                <DropdownMenuLabel>Your Businesses</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {businesses.map((business) => (
                  <DropdownMenuItem key={business.id} className="cursor-pointer group flex items-center justify-between pr-2"
                    data-testid={`business-option-${business.id}`}>
                    <span className="flex-1 truncate" onClick={() => switchBusiness(business.id)}>{business.name}</span>
                    <div className="flex items-center gap-1">
                      {currentBusiness?.id === business.id && <Check className="h-4 w-4 text-primary" />}
                      {businesses.length > 1 && (
                        <button
                          onClick={(e) => openDeleteDialog(e, business)}
                          className="p-1 rounded hover:bg-red-100 text-slate-400 hover:text-red-600 transition-colors ml-1"
                          data-testid={`delete-business-${business.id}`}
                          title="Delete business"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </div>
                  </DropdownMenuItem>
                ))}
                <DropdownMenuSeparator />
                <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                  <DialogTrigger asChild>
                    <DropdownMenuItem onSelect={(e) => e.preventDefault()} className="cursor-pointer text-primary" data-testid="add-business-btn">
                      <Plus className="h-4 w-4 mr-2" /> Add New Business
                    </DropdownMenuItem>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle className="font-heading">Create New Business</DialogTitle>
                      <DialogDescription>Add a new business to manage with TribeHR</DialogDescription>
                    </DialogHeader>
                    <form onSubmit={handleCreateBusiness} className="space-y-4 mt-4">
                      <div className="space-y-2">
                        <Label htmlFor="business-name">Business Name</Label>
                        <Input id="business-name" placeholder="Acme Inc." value={newBusinessName}
                          onChange={(e) => setNewBusinessName(e.target.value)} className="input-field" data-testid="new-business-name-input" />
                      </div>
                      <Button type="submit" className="w-full btn-primary" disabled={isCreating} data-testid="create-business-submit">
                        {isCreating ? "Creating..." : "Create Business"}
                      </Button>
                    </form>
                  </DialogContent>
                </Dialog>
              </DropdownMenuContent>
            </DropdownMenu>
          )}

          {/* Show business name for employees (read-only) */}
          {isEmployee && currentBusiness && (
            <div className="flex items-center gap-2 px-4 py-2 rounded-full border border-slate-200 bg-white/50" data-testid="employee-business-label">
              <Building2 className="h-4 w-4 text-primary" />
              <span className="text-sm font-medium text-slate-700 max-w-[150px] truncate">{currentBusiness.name}</span>
            </div>
          )}
        </div>
      </div>

      {/* Delete Business Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading text-red-600">Delete Business</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete <span className="font-semibold">{businessToDelete?.name}</span>?
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
              <p className="font-medium mb-2">⚠️ Warning: This action cannot be undone!</p>
              <p>Deleting this business will permanently remove:</p>
              <ul className="list-disc ml-4 mt-2 space-y-1">
                <li>All employees and their data</li>
                <li>All attendance records</li>
                <li>All leave requests</li>
                <li>All payroll records</li>
                <li>All documents</li>
                <li>All job postings and applications</li>
              </ul>
            </div>
            <div className="flex gap-3">
              <Button variant="outline" onClick={() => setDeleteDialogOpen(false)} className="flex-1">
                Cancel
              </Button>
              <Button onClick={handleDeleteBusiness} disabled={isDeleting} className="flex-1 bg-red-600 hover:bg-red-700 text-white" data-testid="confirm-delete-business">
                {isDeleting ? "Deleting..." : "Delete Business"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </header>
  );
};

export default Header;
